import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

const MAX_PIN_MS = 1500;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

const isTransitioning = (element: Element) =>
  element.getAnimations({ subtree: true }).some((animation) => 'transitionProperty' in animation);

const gridItems = (list: HTMLElement) =>
  Array.from(list.querySelector('[data-cards-grid]')?.children ?? []).filter(
    (item) => item.getBoundingClientRect().height > 0
  );

const findAnchor = (list: HTMLElement, hotelId?: number) => {
  const items = gridItems(list);
  const opener = hotelId ? list.querySelector(`[data-view-on-map="${hotelId}"]`) : null;
  return (
    items.find((item) => item.contains(opener)) ??
    items.find((item) => item.getBoundingClientRect().bottom > window.innerHeight / 2) ??
    items.at(-1)
  );
};

// Browser scroll anchoring stands down while an ancestor's width animates, so the cards above would push this one away.
const pinWhileTransitioning = (list: HTMLElement, initialAnchor: Element) => {
  let anchor = initialAnchor;
  let { top } = anchor.getBoundingClientRect();
  const deadline = performance.now() + MAX_PIN_MS;
  // Reduced motion lands the new layout in the first frame, with nothing left to wait for.
  let hasTransitioned = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const focusedAtStart = document.activeElement;
  let isFirstFrame = true;
  let frame = 0;

  const stop = () => {
    cancelAnimationFrame(frame);
    USER_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop, true));
  };

  const pin = () => {
    if (isFirstFrame) {
      isFirstFrame = false;
      // Focus may have just scrolled to its card, as when the list returns focus to the card that opened the map.
      const focusedItem =
        document.activeElement !== focusedAtStart &&
        gridItems(list).find((item) => item.contains(document.activeElement));
      if (focusedItem && focusedItem !== anchor) {
        anchor = focusedItem;
        ({ top } = anchor.getBoundingClientRect());
      }
    }
    if (!anchor.isConnected) {
      stop();
      return;
    }
    const drift = anchor.getBoundingClientRect().top - top;
    if (drift) window.scrollBy(0, drift);
    const transitioning = isTransitioning(list.parentElement ?? list);
    hasTransitioned ||= transitioning;
    if ((hasTransitioned && !transitioning) || performance.now() > deadline) {
      stop();
      return;
    }
    frame = requestAnimationFrame(pin);
  };

  USER_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, true));
  frame = requestAnimationFrame(pin);
  return stop;
};

export const useKeepCardInPlace = (listContainerRef: React.MutableRefObject<HTMLDivElement | undefined>) => {
  const stopRef = useRef<() => void>(undefined);

  useEffect(() => () => stopRef.current?.(), []);

  return useCallback(
    (hotelId?: number) => {
      stopRef.current?.();
      const list = listContainerRef.current;
      const anchor = list && findAnchor(list, hotelId);
      stopRef.current = anchor ? pinWhileTransitioning(list, anchor) : undefined;
    },
    [listContainerRef]
  );
};
